import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Tutorial } from '../types/tutorial';

interface TutorialNavigation {
  previous: Pick<Tutorial, 'id' | 'title' | 'slug'> | null;
  next: Pick<Tutorial, 'id' | 'title' | 'slug'> | null;
}

export function useTutorialNavigation(
  tutorialId: string | undefined,
  categorySlug: string | undefined,
  currentOrder: number | undefined
) {
  const [navigation, setNavigation] = useState<TutorialNavigation>({
    previous: null,
    next: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchNavigation() {
      if (!tutorialId || !categorySlug || currentOrder === undefined) {
        setNavigation({ previous: null, next: null });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch previous and next tutorials in parallel
        const [previousResult, nextResult] = await Promise.all([
          // Previous tutorial (highest order less than current)
          supabase
            .from('tutorials')
            .select('id, title, slug')
            .eq('category_slug', categorySlug)
            .eq('published', true)
            .lt('order', currentOrder)
            .order('order', { ascending: false })
            .limit(1)
            .maybeSingle(),

          // Next tutorial (lowest order greater than current)
          supabase
            .from('tutorials')
            .select('id, title, slug')
            .eq('category_slug', categorySlug)
            .eq('published', true)
            .gt('order', currentOrder)
            .order('order', { ascending: true })
            .limit(1)
            .maybeSingle()
        ]);

        if (previousResult.error && previousResult.error.code !== 'PGRST116') {
          throw previousResult.error;
        }
        if (nextResult.error && nextResult.error.code !== 'PGRST116') {
          throw nextResult.error;
        }

        setNavigation({
          previous: previousResult.data,
          next: nextResult.data
        });
      } catch (err) {
        console.error('Error fetching tutorial navigation:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchNavigation();
  }, [tutorialId, categorySlug, currentOrder]);

  return { navigation, loading, error };
}